import { Component, DestroyRef, ElementRef, Input, OnDestroy, OnInit, computed, effect, inject, signal, viewChild } from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { EventconfigStoreService } from '../../shared/eventconfig-store.service';
import { PollStoreService } from '../../shared/poll-store.service';
import { itemPolls, MyVote, PollItem, pollItemLabel, pollTabLabel, pollText, ViewerPoll, VoteOutcome, VotePayload } from '../../shared/poll';

/** Umfang des Kreisdiagramms (r = 40 im viewBox 100×100) */
const DONUT_CIRCUMFERENCE = 2 * Math.PI * 40;
const DONUT_GAP = 1.2;
/** Abstufungen der Theme-Farbe für die Segmente, kräftig → hell */
const DONUT_SHADES = [100, 70, 48, 32, 20, 85, 58, 40, 26, 14];
/** Wie im Backend (TEXT_ANSWER_LENGTH) */
const TEXT_ANSWER_LENGTH = 500;
/** Nach der Antwort in einer Kette kurz stehen lassen, dann zur nächsten offenen Frage */
const ADVANCE_DELAY_MS = 900;

/**
 * Umfragen auf der Eventseite.
 *
 * Gibt es mehrere Umfragen, wechselt man über Tabs zwischen ihnen; vorne steht die offene
 * bzw. jüngste. Geht eine neue Umfrage auf, springt die Ansicht zu ihr.
 *
 * Ketten sind ein Eintrag: ihre Fragen liegen nebeneinander und rasten beim Wischen ein
 * (scroll-snap), dazu Pfeile und ein Punkt je Frage. Nach einer Antwort oder dem Überspringen
 * geht es von selbst zur nächsten offenen Frage. Überspringen geht bei jeder Umfrage und wird
 * gezählt; es ist wie eine Stimme endgültig.
 *
 * Zwei Darstellungen:
 *   tab    Desktop mit rechter Spalte: Inhalt des Umfrage-Tabs neben Präsentation und Fragen.
 *          Den Hinweis auf eine neue Umfrage trägt dort der Tab-Button im Header.
 *   panel  Mobil und ohne rechte Spalte: aufklappbarer Bereich. Unsichtbar, solange es keine
 *          Umfrage gibt; geht eine neue auf, bei der der Zuschauer noch nicht abgestimmt hat,
 *          klappt er auf und trägt ein „Neu"-Abzeichen.
 *
 * Ergebnisse: bei „live" erst nach der eigenen Stimme (oder nach dem Schließen), damit der
 * Zwischenstand niemanden beeinflusst; bei „nach Veröffentlichung" für alle, sobald die
 * Regie sie freigibt; bei „keine Veröffentlichung" nie. Das Endergebnis zeigt je nach
 * Einstellung der Umfrage Balken oder ein Kreisdiagramm. Animationen laufen per CSS und
 * entfallen bei „Bewegung reduzieren".
 *
 * Skala („Wie fühlst du dich von 1 bis 10?"): abgestimmt wird per Schieberegler oder über die
 * Zahlen darunter. Vorausgewählt ist nichts, damit die Mitte nicht als Anker wirkt und niemand
 * versehentlich abstimmt. Das Ergebnis ist der Durchschnitt, je nach Einstellung mit Verteilung.
 */
@Component({
  selector: 'lse-user-poll',
  templateUrl: './user-poll.component.html',
  styleUrls: ['./user-poll.component.css'],
  animations: [
    trigger('slideInOut', [
      state('in', style({ overflow: 'hidden', height: '*' })),
      state('out', style({ overflow: 'hidden', height: '0px' })),
      transition('in => out', animate('250ms ease-in-out')),
      transition('out => in', animate('250ms ease-in-out'))
    ])
  ],
  standalone: false
})
export class UserPollComponent implements OnInit, OnDestroy {
  @Input() eLangGlob: any;
  @Input() mode: 'panel' | 'tab' = 'panel';

  private readonly store = inject(PollStoreService);
  private readonly es = inject(EventconfigStoreService);

  readonly lang = this.es.activeLang;
  readonly polls = this.store.polls;
  readonly myVotes = this.store.myVotes;

  readonly items = this.store.items;
  /** Gewählter Eintrag — im Store, weil auf dem Desktop die Tabs im Seiten-Header stehen */
  readonly displayedItem = this.store.displayedItem;
  /** Nach dem Event: Umfragen erscheinen an ihren Stellen im Video, abgestimmt wird nicht */
  readonly vod = this.store.vodMode;
  /** Frage im Blick; in einer Kette die Karte, auf der das Karussell steht */
  readonly focused = this.store.displayedPoll;
  /** Als Liste, damit @for den Eintrag beim Wechsel neu aufbaut — und seine Einblendung abspielt */
  readonly displayedList = computed(() => {
    const item = this.displayedItem();
    return item ? [item] : [];
  });

  /** Karussell einer Kette */
  private readonly track = viewChild<ElementRef<HTMLElement>>('track');
  private advanceTimer: ReturnType<typeof setTimeout> | null = null;
  readonly textLimit = TEXT_ANSWER_LENGTH;

  readonly expanded = signal(true);
  /** Auswahl vor dem Absenden, je Umfrage */
  readonly selection = signal<Record<string, string[]>>({});
  /** Freie Antwort vor dem Absenden, je Umfrage */
  readonly textDraft = signal<Record<string, string>>({});
  readonly submitting = signal<string | null>(null);
  readonly outcome = signal<Record<string, VoteOutcome>>({});

  /** Offene Umfrage, bei der noch nicht abgestimmt wurde */
  readonly hasNew = this.store.hasUnvotedOpenPoll;

  constructor() {
    // Karussell auf die Frage im Blick fahren — etwa wenn die Regie die nächste Frage öffnet.
    // Beim Wischen setzt onTrackScroll die Frage im Blick; dann steht das Karussell schon dort.
    // Ein neu aufgebautes Karussell (anderer Eintrag gewählt) springt sofort an die Stelle.
    let lastTrack: HTMLElement | null = null;
    effect(() => {
      const track = this.track()?.nativeElement;
      const index = this.focusIndex();
      if (!track || index < 0 || !track.clientWidth) return;
      const fresh = track !== lastTrack;
      lastTrack = track;
      if (Math.round(track.scrollLeft / track.clientWidth) !== index) {
        track.scrollTo({ left: index * track.clientWidth, behavior: fresh || prefersReducedMotion() ? 'auto' : 'smooth' });
      }
    });
    inject(DestroyRef).onDestroy(() => this.advanceTimer && clearTimeout(this.advanceTimer));

    // Als Panel bei neuer Umfrage aufklappen. Pro Umfrage nur einmal, damit ein bewusstes
    // Zuklappen nicht beim nächsten Zählerstand wieder aufgehoben wird.
    effect(() => {
      const unvoted = this.store.firstUnvotedOpenPoll();
      if (this.mode === 'panel' && unvoted && this.markSeen(unvoted.id)) {
        this.expanded.set(true);
      }
    });
  }

  ngOnInit(): void {
    this.store.connect();
  }

  ngOnDestroy(): void {
    this.store.disconnect();
  }

  text(poll: ViewerPoll, value: Record<string, string> | undefined): string {
    return pollText(value, this.lang(), poll.defaultLang);
  }

  tabLabel(poll: ViewerPoll): string {
    return pollTabLabel(poll, this.lang());
  }

  selectItem(item: PollItem): void {
    this.store.selectItem(item);
  }

  /** Beschriftung eines Eintrags: die Frage, bei einer Kette ihr Titel */
  itemLabel(item: PollItem): string {
    return pollItemLabel(item, this.lang(), this.eLangGlob?.translatePollSeries ?? '');
  }

  itemOpen(item: PollItem): boolean {
    return itemPolls(item).some((poll) => poll.status === 'open');
  }

  myVote(poll: ViewerPoll): MyVote | null {
    return this.myVotes()[poll.id] ?? null;
  }

  // ── Kette als Karussell ─────────────────────────────────────────────────────

  /** Position der Frage im Blick unter den sichtbaren Fragen der Kette, sonst -1 */
  readonly focusIndex = computed(() => {
    const item = this.displayedItem();
    const focused = this.focused();
    if (item?.kind !== 'chain' || !focused) return -1;
    return item.polls.findIndex((poll) => poll.id === focused.id);
  });

  goTo(item: PollItem, index: number): void {
    const polls = itemPolls(item);
    const target = polls[Math.max(0, Math.min(polls.length - 1, index))];
    if (target) this.store.selectPoll(target.id);
  }

  /** Punkt einer Frage angetippt — nur Fragen, die schon sichtbar sind */
  goToSeriesStep(item: PollItem, step: number): void {
    const index = itemPolls(item).findIndex((poll) => poll.series?.index === step);
    if (index >= 0) this.goTo(item, index);
  }

  seriesStepVisible(item: PollItem, step: number): boolean {
    return itemPolls(item).some((poll) => poll.series?.index === step);
  }

  /** Beim Wischen: Frage im Blick nachziehen, sobald das Karussell auf einer Karte einrastet */
  onTrackScroll(item: PollItem, track: HTMLElement): void {
    if (!track.clientWidth) return;
    const index = Math.round(track.scrollLeft / track.clientWidth);
    const poll = itemPolls(item)[index];
    if (poll && poll.id !== this.focused()?.id) this.store.selectPoll(poll.id);
  }

  /** Nach Antwort oder Überspringen: zur nächsten offenen Frage dieser Kette, bei der noch nichts steht */
  private advanceAfterAnswer(poll: ViewerPoll): void {
    const item = this.displayedItem();
    if (item?.kind !== 'chain' || !item.polls.some((entry) => entry.id === poll.id)) return;
    const after = item.polls.slice(item.polls.indexOf(poll) + 1);
    const next = after.find((entry) => entry.status === 'open' && !this.myVote(entry));
    if (!next) return;
    if (this.advanceTimer) clearTimeout(this.advanceTimer);
    this.advanceTimer = setTimeout(() => {
      this.advanceTimer = null;
      if (this.focused()?.id === poll.id) this.store.selectPoll(next.id);
    }, ADVANCE_DELAY_MS);
  }

  isSelected(poll: ViewerPoll, optionId: string): boolean {
    return (this.selection()[poll.id] ?? []).includes(optionId);
  }

  hasSelection(poll: ViewerPoll): boolean {
    if (poll.type === 'text') return !!this.textDraft()[poll.id]?.trim();
    return (this.selection()[poll.id] ?? []).length > 0;
  }

  setText(poll: ViewerPoll, value: string): void {
    this.textDraft.update((drafts) => ({ ...drafts, [poll.id]: value }));
  }

  /** Bei Mehrfachauswahl: weitere Optionen sperren, sobald das Maximum erreicht ist */
  isBlocked(poll: ViewerPoll, optionId: string): boolean {
    if (poll.type !== 'multiple' || !poll.maxChoices) return false;
    const chosen = this.selection()[poll.id] ?? [];
    return chosen.length >= poll.maxChoices && !chosen.includes(optionId);
  }

  toggle(poll: ViewerPoll, optionId: string): void {
    this.selection.update((selection) => {
      const chosen = selection[poll.id] ?? [];
      let next: string[];
      if (poll.type === 'single') {
        next = [optionId];
      } else if (chosen.includes(optionId)) {
        next = chosen.filter((id) => id !== optionId);
      } else if (this.isBlocked(poll, optionId)) {
        next = chosen;
      } else {
        next = [...chosen, optionId];
      }
      return { ...selection, [poll.id]: next };
    });
  }

  async submit(poll: ViewerPoll): Promise<void> {
    if (!this.hasSelection(poll)) return;
    const payload: VotePayload = poll.type === 'text'
      ? { text: (this.textDraft()[poll.id] ?? '').trim() }
      : { optionIds: this.selection()[poll.id] ?? [] };
    await this.send(poll, payload);
  }

  /** Überspringen wird mitgezählt und ist wie eine Stimme endgültig */
  async skip(poll: ViewerPoll): Promise<void> {
    await this.send(poll, { skip: true });
  }

  private async send(poll: ViewerPoll, payload: VotePayload): Promise<void> {
    if (this.submitting()) return;
    this.submitting.set(poll.id);
    const result = await this.store.vote(poll.id, payload);
    this.submitting.set(null);
    this.outcome.update((outcome) => ({ ...outcome, [poll.id]: result }));
    if (result === 'ok') this.advanceAfterAnswer(poll);
  }

  /**
   * Ergebnis zeigen? Bei „live" erst nach der eigenen Stimme oder dem Schließen;
   * bei „nach Veröffentlichung" schickt der Server die Zahlen ohnehin erst dann.
   */
  showResults(poll: ViewerPoll): boolean {
    if (!poll.resultsVisible || !poll.results) return false;
    if (poll.resultsVisibility === 'onPublish') return true;
    return poll.status === 'closed' || (!!this.myVote(poll) && !this.myVote(poll)?.skipped);
  }

  /** Kreisdiagramm nur für das Endergebnis — während der Abstimmung bleiben es Balken */
  showDonut(poll: ViewerPoll): boolean {
    return poll.type !== 'scale' && poll.resultsChart === 'donut' && poll.status === 'closed';
  }

  // ── Skala ──────────────────────────────────────────────────────────────────

  /** Gewählter Wert vor dem Absenden, null = noch nichts gewählt */
  scaleValue(poll: ViewerPoll): number | null {
    const chosen = (this.selection()[poll.id] ?? [])[0];
    return poll.options.find((option) => option.id === chosen)?.value ?? null;
  }

  setScaleValue(poll: ViewerPoll, value: number): void {
    const option = poll.options.find((entry) => entry.value === value);
    if (option) this.selection.update((selection) => ({ ...selection, [poll.id]: [option.id] }));
  }

  scaleValues(poll: ViewerPoll): number[] {
    return poll.options.map((option) => option.value ?? 0);
  }

  /** Lage eines Werts auf der Skala: 0 am Anfang, 1 am Ende */
  scaleFraction(poll: ViewerPoll, value: number | null): number {
    if (value === null || !poll.scale || poll.scale.max === poll.scale.min) return 0;
    return (value - poll.scale.min) / (poll.scale.max - poll.scale.min);
  }

  /** Ausgangspunkt der Skala: 0, wenn sie 0 enthält (z. B. -5 bis 5), sonst ihr Anfang */
  scaleOrigin(poll: ViewerPoll): number {
    const scale = poll.scale;
    if (!scale) return 0;
    return scale.min <= 0 && scale.max >= 0 ? 0 : scale.min;
  }

  /** Gefüllter Teil der Spur: vom Ausgangspunkt bis zum Wert, nach links oder rechts */
  scaleFill(poll: ViewerPoll, value: number | null): { lo: number; hi: number } {
    if (value === null) return { lo: 0, hi: 0 };
    const origin = this.scaleFraction(poll, this.scaleOrigin(poll));
    const position = this.scaleFraction(poll, value);
    return { lo: Math.min(origin, position), hi: Math.max(origin, position) };
  }

  /** Mittelpunkt der Skala, falls es einen gibt (gerade Spannweite) */
  scaleMid(poll: ViewerPoll): number | null {
    const scale = poll.scale;
    if (!scale || (scale.max - scale.min) % 2 !== 0) return null;
    return scale.min + (scale.max - scale.min) / 2;
  }

  /** Beschriftung der Mitte — nur, wenn es eine Mitte gibt */
  scaleMidLabel(poll: ViewerPoll): string {
    return this.scaleMid(poll) === null ? '' : this.text(poll, poll.scale?.midLabel);
  }

  /** Durchschnitt der abgegebenen Werte, null ohne Stimmen */
  average(poll: ViewerPoll): number | null {
    const counts = poll.results?.counts ?? {};
    let votes = 0;
    let sum = 0;
    for (const option of poll.options) {
      const count = counts[option.id] ?? 0;
      votes += count;
      sum += count * (option.value ?? 0);
    }
    return votes ? sum / votes : null;
  }

  /** Am Ende nur der Durchschnitt; solange abgestimmt wird, immer mit Verteilung */
  showAverageOnly(poll: ViewerPoll): boolean {
    return poll.resultsChart === 'average' && poll.status === 'closed';
  }

  rows(poll: ViewerPoll) {
    const counts = poll.results?.counts ?? {};
    const voters = poll.results?.voterCount ?? 0;
    const mine = this.myVote(poll)?.optionIds ?? [];
    const max = Math.max(0, ...poll.options.map((option) => counts[option.id] ?? 0));
    return poll.options.map((option) => {
      const count = counts[option.id] ?? 0;
      return {
        id: option.id,
        label: this.text(poll, option.label),
        count,
        percent: voters ? Math.round((count / voters) * 100) : 0,
        // Säulenhöhe der Skala relativ zum häufigsten Wert
        height: max ? Math.round((count / max) * 100) : 0,
        mine: mine.includes(option.id),
        // Führende Antwort erst hervorheben, wenn das Ergebnis feststeht
        leader: poll.status === 'closed' && max > 0 && count === max
      };
    });
  }

  /**
   * Segmente des Kreisdiagramms. Anteile nach Stimmen je Antwort — bei Mehrfachauswahl also
   * an allen abgegebenen Antworten; die Legende zeigt wie die Balken den Anteil der Abstimmenden.
   */
  donut(poll: ViewerPoll) {
    const rows = this.rows(poll);
    const total = rows.reduce((sum, row) => sum + row.count, 0);
    let offset = 0;
    return rows.map((row, index) => {
      const length = total ? (row.count / total) * DONUT_CIRCUMFERENCE : 0;
      const visible = Math.max(0, length - (length > DONUT_GAP * 2 ? DONUT_GAP : 0));
      const segment = {
        ...row,
        color: `color-mix(in srgb, var(--color-primary, #cd1316) ${DONUT_SHADES[index % DONUT_SHADES.length]}%, #ffffff)`,
        dash: `${visible} ${DONUT_CIRCUMFERENCE - visible}`,
        offset: -offset
      };
      offset += length;
      return segment;
    });
  }

  chosenLabels(poll: ViewerPoll): string {
    const vote = this.myVote(poll);
    if (poll.type === 'text') return vote?.text ?? '';
    const mine = vote?.optionIds ?? [];
    return poll.options
      .filter((option) => mine.includes(option.id))
      .map((option) => this.text(poll, option.label))
      .join(', ');
  }

  /** Kette: „Frage 2 von 5" */
  seriesProgress(poll: ViewerPoll): string {
    const template: string = this.eLangGlob?.translatePollSeriesProgress ?? '';
    return poll.series ? template.replace('{n}', String(poll.series.index)).replace('{total}', String(poll.series.total)) : '';
  }

  /** Kette: Nummern 1 … total für die Punkte — auch für Fragen, die noch nicht veröffentlicht sind */
  seriesStepsOf(item: PollItem): number[] {
    const total = itemPolls(item)[0]?.series?.total ?? itemPolls(item).length;
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  /** Erledigt: abgestimmt, übersprungen oder beendet */
  seriesStepDone(item: PollItem, step: number): boolean {
    const poll = itemPolls(item).find((entry) => entry.series?.index === step);
    return !!poll && (!!this.myVote(poll) || poll.status === 'closed');
  }

  seriesStepLabel(step: number, item: PollItem): string {
    const template: string = this.eLangGlob?.translatePollSeriesProgress ?? '';
    const total = this.seriesStepsOf(item).length;
    return template.replace('{n}', String(step)).replace('{total}', String(total));
  }

  /** Nach der Stimme in einer Kette Frage für Frage: die Regie schaltet gleich weiter */
  hasNextInSeries(poll: ViewerPoll): boolean {
    return !!poll.series && poll.series.mode !== 'all' && poll.series.index < poll.series.total && poll.status === 'open';
  }

  maxChoicesHint(poll: ViewerPoll): string {
    const template: string = this.eLangGlob?.translatePollMaxChoices ?? '';
    return poll.maxChoices ? template.replace('{n}', String(poll.maxChoices)) : (this.eLangGlob?.translatePollMultiple ?? '');
  }

  toggleExpanded(): void {
    this.expanded.update((expanded) => !expanded);
  }

  /** true, wenn die Umfrage in dieser Sitzung noch nicht gesehen wurde */
  private markSeen(pollId: string): boolean {
    const key = `lse-polls-seen-${this.es.token}`;
    try {
      const seen: string[] = JSON.parse(sessionStorage.getItem(key) ?? '[]');
      if (seen.includes(pollId)) return false;
      sessionStorage.setItem(key, JSON.stringify([...seen, pollId]));
    } catch {
      // Ohne sessionStorage klappt der Bereich eben jedes Mal auf.
    }
    return true;
  }
}

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}
